<template>
	<div class="search-page">
		<MainColumn>
			<div class="search-page-content">
				<h1 class="page-title">Cerca</h1>
				
				<!-- Campo di ricerca -->
				<div class="search-input-wrapper">
					<input
						type="text"
						v-model="searchQuery"
						@input="onSearchInput"
						placeholder="Inizia a digitare per trovare risultati"
						class="search-input"
					/>
					<button 
						v-if="searchQuery" 
						@click="clearSearch" 
						class="clear-button"
					>
						×
					</button>
				</div>

				<!-- Loader -->
				<div v-if="loading" class="loading">
					Ricerca in corso...
				</div>

				<!-- Messaggio nessun risultato -->
				<div v-if="!loading && searchQuery && results.length === 0" class="no-results">
					Nessun risultato trovato per "<strong>{{ searchQuery }}</strong>"
				</div>

				<!-- Lista risultati -->
				<div v-if="!loading && results.length > 0" class="results-list">
					<p class="results-count">
						{{ results.length }} risultat{{ results.length === 1 ? 'o' : 'i' }} per "<strong>{{ searchQuery }}</strong>"
					</p>
					
					<div 
						v-for="(result, index) in results" 
						:key="index"
						class="result-item"
						:class="'target-' + (result.target || 'default')"
						@click="goToResult(result)"
					>
						<div class="result-content">
							
							<h2 
								class="result-title" 
								v-html="highlightText(result.title)"
							></h2>
							
							<p 
								v-if="result.excerpt" 
								class="result-excerpt" 
								v-html="highlightText(stripHtml(result.excerpt))"
							></p>
							
							<div class="result-meta">
								<span v-if="result.date" class="result-date">
									{{ formatDate(result.date) }}
								</span>
								<span v-if="result.author" class="result-author">
									{{ result.author }}
								</span>
								<span v-if="result.categories" class="result-categories">
									{{ result.categories }}
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</MainColumn>
	</div>
</template>

<script>
import MainColumn from "@/components/ui/MainColumn.vue";

export default {
	name: "SearchPage",
	components: {
		MainColumn,
	},
	data() {
		return {
			searchQuery: "",
			results: [],
			loading: false,
			debounceTimer: null,
			apiUrl: "/wp-json/wp/v2", // Adatta se necessario
		};
	},
	mounted() {
		// Se c'è un parametro di ricerca nell'URL
		if (this.$route.query.q) {
			this.searchQuery = this.$route.query.q;
			this.performSearch();
		}
	},
	methods: {
		onSearchInput() {
			// Debounce per evitare troppe chiamate
			clearTimeout(this.debounceTimer);
			this.debounceTimer = setTimeout(() => {
				if (this.searchQuery.length >= 3) {
					this.performSearch();
				} else if (this.searchQuery.length === 0) {
					this.results = [];
				}
			}, 500);
		},
		
		async performSearch() {
			if (!this.searchQuery || this.searchQuery.length < 3) {
				return;
			}

			this.loading = true;

			try {
				// Chiamata alle REST API di WordPress
				const response = await fetch(
					`https://bibliotecaagora.it/wp-json/wp/v2/posts?search=${encodeURIComponent(this.searchQuery)}&per_page=50&_embed`
				);

				if (!response.ok) {
					throw new Error("Errore nella ricerca");
				}

				const posts = await response.json();
				
				// Mappo i risultati
				this.results = posts.map(post => {
					// Estraggo le categorie
					const categories = post._embedded?.['wp:term']?.[0]
						?.map(cat => cat.name)
						.join(', ') || '';

					// Estraggo l'autore
					const author = post._embedded?.author?.[0]?.name || '';

					// Estraggo il target (adatta il campo secondo la tua configurazione)
					const target = post.qzr_target || post.acf?.qzr_target || null;

					return {
						id: post.id,
						title: post.title.rendered,
						excerpt: post.excerpt.rendered,
						date: post.date,
						link: post.link,
						slug: post.slug,
						author: author,
						categories: categories,
						target: target,
					};
				});

			} catch (error) {
				console.error("Errore durante la ricerca:", error);
				this.results = [];
			} finally {
				this.loading = false;
			}
		},

		clearSearch() {
			this.searchQuery = "";
			this.results = [];
		},

		highlightText(text) {
			if (!this.searchQuery || !text) return text;
			
			const searchTerms = this.searchQuery
				.toLowerCase()
				.split(" ")
				.filter(term => term.length > 2);
			
			let highlighted = text;
			
			searchTerms.forEach(term => {
				const regex = new RegExp(`(${term})`, "gi");
				highlighted = highlighted.replace(
					regex, 
					'<mark>$1</mark>'
				);
			});
			
			return highlighted;
		},

		stripHtml(html) {
			const tmp = document.createElement("div");
			tmp.innerHTML = html;
			return tmp.textContent || tmp.innerText || "";
		},

		formatDate(dateString) {
			const date = new Date(dateString);
			return date.toLocaleDateString("it-IT", {
				year: "numeric",
				month: "long",
				day: "numeric",
			});
		},

		getTargetColor(targetSlug) {
			// Adatta questi colori ai tuoi target
			const colors = {
				"bambini-e-famiglie": "#FF6B9D",
				"ragazzi": "#4ECDC4",
				"adulti": "#95E1D3",
				"scuole": "#FFD93D",
				"default": "#999",
			};
			return colors[targetSlug] || colors.default;
		},

		formatTargetName(targetSlug) {
			// Converte lo slug in nome leggibile
			const names = {
				"bambini-e-famiglie": "Bambini e Famiglie",
				"ragazzi": "Ragazzi",
				"adulti": "Adulti",
				"scuole": "Scuole",
			};
			return names[targetSlug] || targetSlug;
		},

		goToResult(result) {
			// Puoi usare il link diretto o costruire il path con Vue Router
			window.location.href = result.link;
			// Oppure se usi Vue Router:
			// this.$router.push('/' + result.slug);
		},
	},
};
</script>

<style scoped lang="scss">
.search-page {
	background-color: #f5f5f5;
	min-height: 80vh;
	padding: $spacing-1 0;
}

.search-page-content {
	padding: $spacing-1 0;
}

.page-title {
	font-size: 48px;
	margin-bottom: $spacing-1;
	color: #000;
}

/* Campo di ricerca */
.search-input-wrapper {
	position: relative;
	margin-bottom: $spacing-1;
}

.search-input {
	width: 100%;
	padding: 20px 60px 20px 20px;
	font-size: 18px;
	border: 2px solid #000;
	border-radius: 90px;
	outline: none;
	transition: all 0.3s;
	background: white;

	&:focus {
		border-color: #333;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	}

	&::placeholder {
		color: #999;
	}
}

.clear-button {
	position: absolute;
	right: 20px;
	top: 50%;
	transform: translateY(-50%);
	background: transparent;
	border: none;
	font-size: 32px;
	color: #999;
	cursor: pointer;
	padding: 0;
	width: 32px;
	height: 32px;
	line-height: 1;
	transition: color 0.3s;

	&:hover {
		color: #000;
	}
}

/* Stati */
.loading {
	text-align: center;
	padding: $spacing-1 0;
	font-size: 18px;
	color: #666;
}

.no-results {
	text-align: center;
	padding: $spacing-1 0;
	font-size: 18px;
	color: #666;
}

.results-count {
	color: #666;
	font-size: 14px;
	margin-bottom: 2rem !important;
}

/* Lista risultati */
.results-list {
	margin-top: $spacing-1;
}

.result-item {
	background: white;
	padding: $spacing-0;
	margin-bottom: $spacing-0;
	border-radius: 12px;
	border-top-right-radius: 0;
	cursor: pointer;
	transition: all 0.3s;
	border: 2px solid transparent;

	&:hover {
		box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
		border-color: #000;
	}
}

.result-content {
	position: relative;
}

.result-target {
	display: inline-block;
	padding: 4px 12px;
	border-radius: 12px;
	border-top-right-radius: 0;
	font-size: 11px;
	text-transform: uppercase;
	font-weight: bold;
	color: white;
	margin-bottom: 8px;
	letter-spacing: 0.5px;
}

.result-title {
	font-size: 24px;
	margin: 8px 0;
	color: #000;
	line-height: 1.3;

	::v-deep mark {
		background-color: #FFE66D;
		color: #000;
		padding: 2px 4px;
		border-radius: 3px;
	}
}

.result-excerpt {
	font-size: 14px;
	color: #666;
	line-height: 1.6;
	margin: 8px 0;
	
	::v-deep mark {
		background-color: #FFE66D;
		color: #000;
		padding: 2px 4px;
		border-radius: 3px;
	}
}

.result-meta {
	display: flex;
	gap: 16px;
	margin-top: 12px;
	font-size: 12px;
	color: #999;
	flex-wrap: wrap;

	span {
		&:not(:last-child)::after {
			content: "•";
			margin-left: 16px;
			color: #ddd;
		}
	}
}

/* Responsive */
@media only screen and (max-width: 768px) {
	.page-title {
		font-size: 32px;
	}

	.search-input {
		padding: 16px 50px 16px 16px;
		font-size: 16px;
	}

	.result-title {
		font-size: 20px;
	}

	.result-meta {
		flex-direction: column;
		gap: 4px;

		span::after {
			display: none;
		}
	}
}
</style>

<style lang="scss">
	.v-application {
		.menu-cerca {
			*:not(.button, .main-column) {
				color: black !important;
				border-color: black !important;
			}
			.bar-icon {
				background-color: black !important;
			}
		}
	}

</style>